import {
  React,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import { motion } from "framer-motion";
import PageWrapper from "../pageWrapper";

//CSS
import "./warehouseEditAndCreatePage.css";
import "../0PageStyles.css";

//Components
import Button from "../../Components/Other Components/button";
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import Title from "../../Components/Other Components/Title";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

//Functions and hooks
import {
  CreateWarehouse,
  GetNextWarehouseID,
  GetWarehouseCount,
} from "../../BackendAccess/WarehouseHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useTranslation } from "react-i18next";

export default function WarehouseCreationPage() {
  // State variables
  const [Name, setFieldName] = useState("");
  const [ID, setFieldID] = useState("");
  const [Address, setFieldAddress] = useState("");
  const [image, setImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [createDisabled, setCreateDisabled] = useState(false);

  const isMounted = useRef(true);

  const { showNotification } = useNotification();

  const navigate = useNavigate();

  // Use both common and warehouse namespaces
  const { t } = useTranslation(["common", "warehouse", "error"]);

  const fetchInitialValues = useCallback(async () => {
    if (!isMounted.current) return;

    setLoading(true);
    try {
      const [data, error] = await fetchWithTimeout(GetNextWarehouseID);
      if (!isMounted.current) return;

      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setFieldID(data);
      }

      const [count, countError] = await fetchWithTimeout(GetWarehouseCount);
      if (!isMounted.current) return;

      if (countError) {
        setError(countError);
      } else {
        setFieldName(t(`Warehouse`) + ` ${count + 1}`);
      }
    } catch (err) {
      if (isMounted.current) {
        setError({
          errCode: 500,
          errMsg: t("server.Server error", { ns: "error" }),
        });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    isMounted.current = true;
    fetchInitialValues();

    return () => {
      isMounted.current = false;
    };
  }, [fetchInitialValues]);

  useErrorHandler(error, showNotification, setPageError);

  const handleImageChange = useCallback((file) => {
    setImage(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isMounted.current) {
          setPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleIDChange = useCallback(
    (value) => {
      setFieldID(value);
    },
    [t]
  );

  const handleNameChange = useCallback((value) => {
    setFieldName(value);
  }, []);

  const handleAddressChange = useCallback((value) => {
    setFieldAddress(value);
  }, []);

  const OnCreate = useCallback(async () => {
    if (!isMounted.current) return;

    setCreateDisabled(true);
    showNotification(
      t("Warehouse creation in progress...", { ns: "warehouse" }),
      "info"
    );

    try {
      setLoading(true);
      const [result, createError] = await CreateWarehouse(
        image,
        Name,
        ID,
        Address
      );

      if (!isMounted.current) return;

      if (createError === null) {
        showNotification(
          t("Warehouse successfully created!", { ns: "warehouse" }),
          "success"
        );
        navigate(-1);
      } else {
        setError(createError);
      }
    } catch (err) {
      if (isMounted.current) {
        setError({
          err: t("server:Server error.", { ns: "error" }),
          errCode: 500,
        });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setCreateDisabled(false);
      }
    }
  }, [image, Name, ID, Address, navigate, showNotification, t]);

  const clickOnCancel = useCallback(() => {
    showNotification(
      t("Warehouse creation cancelled.", { ns: "warehouse" }),
      "info"
    );
    navigate(-1);
  }, [navigate, showNotification, t]);

  const tutorialSteps = [
    {
      target: null,
      title: t("Creating a new warehouse", { ns: "tutorial" }),
      message: t(
        "This page creates a new warehouse - a physical location where you store items. Every item's stock is tracked separately per warehouse.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-creation-image",
      title: t("Upload Image"),
      message: t(
        "Optional. Add a photo of the warehouse so it's easy to recognize at a glance in the warehouse list.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-creation-id",
      title: t("Warehouse ID", { ns: "tutorial" }),
      message: t(
        "The warehouse's ID. Unlike an item's ID, this one is fully automatic and can't be edited.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-creation-name",
      title: t("Name", { ns: "tutorial" }),
      message: t(
        "Give the warehouse a name you'll recognize - this is what shows up throughout the app whenever this location is referenced.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-creation-address",
      title: t("Address", { ns: "tutorial" }),
      message: t(
        "Optional. The warehouse's physical address, for your own reference.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "warehouse-creation-create-btn",
      title: t("Save the warehouse", { ns: "tutorial" }),
      message: t(
        "Once everything looks right, click here to create the warehouse. You'll then be able to transfer or stock items into it.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="warehouseCreation" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <>
          <div className="page-content">
            <Title className="m-3">{t("Create Warehouse")}</Title>

            <div className="container-bordered horizontal w-90 mb-2">
              <div className="container-empty file-upload">
                <InputField
                  type={"file"}
                  previewImage={preview}
                  onChange={handleImageChange}
                  label={t("Upload Image")}
                  tutorialId="warehouse-creation-image"
                />
              </div>

              <InputGroup className="container-empty vertical warehouse-creation">
                <InputField
                  placeholder={t("Example: 1001")}
                  onChange={handleIDChange}
                  value={ID}
                  label={t("ID") + ":"}
                  mandatory={true}
                  description={t("The custom integer ID of your warehouse", {
                    ns: "warehouse",
                  })}
                  readOnly={true}
                  tutorialId="warehouse-creation-id"
                />
                <div className="spacer l"></div>
                <InputField
                  placeholder={t("Example: Warehouse1", { ns: "warehouse" })}
                  onChange={handleNameChange}
                  value={Name}
                  label={t("Name") + ":"}
                  mandatory={true}
                  description={t("The name of your warehouse", {
                    ns: "warehouse",
                  })}
                  tutorialId="warehouse-creation-name"
                />
                <div className="spacer l"></div>
                <InputField
                  placeholder={t("Example: City1, Street23 15", {
                    ns: "warehouse",
                  })}
                  onChange={handleAddressChange}
                  value={Address}
                  label={t("Address") + ":"}
                  description={t("The address of your warehouse", {
                    ns: "warehouse",
                  })}
                  tutorialId="warehouse-creation-address"
                />
                <MandatoryExplanation />
              </InputGroup>
            </div>
            <div className="btn-container">
              <Button
                btnMargin={"btn-default"}
                btnStyle={"btn-positive"}
                onClick={OnCreate}
                disabled={createDisabled}
                acceptEnter={true}
                icon={"create"}
                tutorialId="warehouse-creation-create-btn"
              >
                {t("Create")}
              </Button>
              <Button
                btnMargin={"btn-default"}
                btnStyle="btn-primary"
                onClick={clickOnCancel}
              >
                {t("Cancel")}
              </Button>
            </div>
          </div>
        </>
      </PageLoader>
    </PageWrapper>
  );
}
